/**
 * pptxFontLoader.ts
 *
 * 어떤 PPTX 파일이든 사용된 폰트를 감지하고,
 * Google Fonts에서 사용 가능한 폰트는 동적으로 로드하며,
 * 없는 폰트는 적절한 대체 폰트로 매핑하는 범용 유틸리티.
 */
import '../assets/fonts/kopub/fonts.css';

// ─────────────────────────────────────────────────────────────────────────────
// Google Fonts에 존재하는 폰트 목록 (폰트명 → Google Fonts 패밀리명)
// 대소문자 무시 비교를 위해 소문자로 키 저장
// ─────────────────────────────────────────────────────────────────────────────
const GOOGLE_FONTS_MAP: Record<string, string> = {
  // 한국어
  'noto sans kr': 'Noto Sans KR',
  'noto serif kr': 'Noto Serif KR',

  // 일본어
  'noto sans jp': 'Noto Sans JP',
  'noto serif jp': 'Noto Serif JP',

  // 중국어 간체
  'noto sans sc': 'Noto Sans SC',
  'noto serif sc': 'Noto Serif SC',

  // 중국어 번체
  'noto sans tc': 'Noto Sans TC',
  'noto serif tc': 'Noto Serif TC',

  // 장식/디스플레이
  'lemon': 'Lemon',
  'lobster': 'Lobster',
  'pacifico': 'Pacifico',
  'dancing script': 'Dancing Script',
  'great vibes': 'Great Vibes',
  'satisfy': 'Satisfy',
  'permanent marker': 'Permanent Marker',

  // 산세리프
  'roboto': 'Roboto',
  'open sans': 'Open Sans',
  'lato': 'Lato',
  'montserrat': 'Montserrat',
  'poppins': 'Poppins',
  'nunito': 'Nunito',
  'raleway': 'Raleway',
  'ubuntu': 'Ubuntu',
  'source sans pro': 'Source Sans Pro',
  'pt sans': 'PT Sans',
  'oswald': 'Oswald',
  'inter': 'Inter',
  'outfit': 'Outfit',

  // 세리프
  'merriweather': 'Merriweather',
  'playfair display': 'Playfair Display',
  'source serif pro': 'Source Serif Pro',
  'pt serif': 'PT Serif',
  'lora': 'Lora',
  'eb garamond': 'EB Garamond',
  'crimson text': 'Crimson Text',
  'cormorant garamond': 'Cormorant Garamond',

  // 모노
  'roboto mono': 'Roboto Mono',
  'source code pro': 'Source Code Pro',
  'fira code': 'Fira Code',
  'space mono': 'Space Mono',
  'jetbrains mono': 'JetBrains Mono',
  'inconsolata': 'Inconsolata',

  // 기타
  'noto sans': 'Noto Sans',
  'noto serif': 'Noto Serif',
  'carlito': 'Carlito',
  'caladea': 'Caladea',
  'arimo': 'Arimo',
  'tinos': 'Tinos',
  'cousine': 'Cousine',
};

// ─────────────────────────────────────────────────────────────────────────────
// 시스템 폰트 (로드 불필요, 모든 OS에서 사용 가능)
// ─────────────────────────────────────────────────────────────────────────────
const SYSTEM_FONTS = new Set([
  'arial', 'arial black', 'arial narrow', 'arial rounded mt bold',
  'helvetica', 'helvetica neue',
  'times new roman', 'times',
  'georgia',
  'trebuchet ms',
  'verdana',
  'tahoma',
  'courier new', 'courier',
  'comic sans ms',
  'impact',
  'calibri', 'calibri light',
  'cambria', 'cambria math',
  'candara',
  'consolas',
  'constantia',
  'corbel',
  'franklin gothic medium',
  'gill sans mt',
  'lucida console', 'lucida sans', 'lucida sans unicode',
  'palatino linotype',
  'symbol',
  'wingdings',
  'century gothic',
  'book antiqua',
  '-apple-system', 'system-ui', 'segoe ui', 'segoe ui historic',
]);

// ─────────────────────────────────────────────────────────────────────────────
// Microsoft Office / Windows 폰트 → metric-compatible 웹폰트 매핑
// Linux/CI 환경에서 로컬 폰트가 없을 때도 원본과 최대한 비슷하게 맞춘다.
// ─────────────────────────────────────────────────────────────────────────────
const OFFICE_METRIC_FALLBACK_MAP: Record<string, string> = {
  'calibri': 'Carlito',
  'calibri light': 'Carlito',
  'cambria': 'Caladea',
  'cambria math': 'Caladea',
  'arial': 'Arimo',
  'arial narrow': 'Arimo',
  'helvetica': 'Arimo',
  'helvetica neue': 'Arimo',
  'times new roman': 'Tinos',
  'times': 'Tinos',
  'courier new': 'Cousine',
  'courier': 'Cousine',
};

// ─────────────────────────────────────────────────────────────────────────────
// Windows/Office 한글 폰트 → Noto Sans KR 대체 매핑
// ─────────────────────────────────────────────────────────────────────────────
const KOREAN_FONT_FALLBACK_MAP: Record<string, string> = {
  'malgun gothic': 'Noto Sans KR',
  '맑은 고딕': 'Noto Sans KR',
  'malgun gothic semilight': 'Noto Sans KR',
  '맑은 고딕 semilight': 'Noto Sans KR',
  'gulim': 'Noto Sans KR',
  '굴림': 'Noto Sans KR',
  'gulimche': 'Noto Sans KR',
  '굴림체': 'Noto Sans KR',
  'dotum': 'Noto Sans KR',
  '돋움': 'Noto Sans KR',
  'dotumche': 'Noto Sans KR',
  '돋움체': 'Noto Sans KR',
  'batang': 'Noto Serif KR',
  '바탕': 'Noto Serif KR',
  'batangche': 'Noto Serif KR',
  '바탕체': 'Noto Serif KR',
  'gungsuh': 'Noto Serif KR',
  '궁서': 'Noto Serif KR',
  'gungsuhche': 'Noto Serif KR',
  '궁서체': 'Noto Serif KR',
  'nanum gothic': 'Nanum Gothic',
  'nanum myeongjo': 'Nanum Myeongjo',
  'nanum gothic coding': 'Nanum Gothic Coding',
  'nanum barun gothic': 'Nanum Gothic',
};

// ─────────────────────────────────────────────────────────────────────────────
// Windows/Office 일본어 폰트 → Noto Sans JP 대체 매핑
// ─────────────────────────────────────────────────────────────────────────────
const JAPANESE_FONT_FALLBACK_MAP: Record<string, string> = {
  'ms gothic': 'Noto Sans JP',
  'ms pgothic': 'Noto Sans JP',
  'ｍｓ ｐゴシック': 'Noto Sans JP',
  'ms ui gothic': 'Noto Sans JP',
  'ms mincho': 'Noto Serif JP',
  'ms pmincho': 'Noto Serif JP',
  'meiryo': 'Noto Sans JP',
  'meiryo ui': 'Noto Sans JP',
  'yu gothic': 'Noto Sans JP',
  'yu mincho': 'Noto Serif JP',
  'hiragino sans': 'Noto Sans JP',
  'hiragino kaku gothic pro': 'Noto Sans JP',
  'hiragino mincho pro': 'Noto Serif JP',
};

// ─────────────────────────────────────────────────────────────────────────────
// Windows/Office 중국어 폰트 → Noto Sans SC/TC 대체 매핑
// ─────────────────────────────────────────────────────────────────────────────
const CHINESE_FONT_FALLBACK_MAP: Record<string, string> = {
  'simsun': 'Noto Serif SC',
  '宋体': 'Noto Serif SC',
  'simhei': 'Noto Sans SC',
  '黑体': 'Noto Sans SC',
  'microsoft yahei': 'Noto Sans SC',
  'microsoft yahei ui': 'Noto Sans SC',
  '微软雅黑': 'Noto Sans SC',
  'fang song': 'Noto Serif SC',
  '仿宋': 'Noto Serif SC',
  'kaiti': 'Noto Serif SC',
  '楷体': 'Noto Serif SC',
  'mingliu': 'Noto Serif TC',
  '細明體': 'Noto Serif TC',
  '新細明體': 'Noto Serif TC',
  'microsoft jhenghei': 'Noto Sans TC',
  '微軟正黑體': 'Noto Sans TC',
};

// Nanum 폰트 (Google Fonts에 존재)
const NANUM_FONTS_MAP: Record<string, string> = {
  'nanum gothic': 'Nanum Gothic',
  'nanum myeongjo': 'Nanum Myeongjo',
  'nanum gothic coding': 'Nanum Gothic Coding',
  'nanum pen script': 'Nanum Pen Script',
  'nanum brush script': 'Nanum Brush Script',
};

// Google Fonts 로드 시 가중치 설정 (폰트별 최적 weight 범위)
const FONT_WEIGHTS: Record<string, string> = {
  'Noto Sans KR': '100;300;400;500;700;900',
  'Noto Serif KR': '200;300;400;500;600;700;900',
  'Noto Sans JP': '100;300;400;500;700;900',
  'Noto Serif JP': '200;300;400;500;600;700;900',
  'Noto Sans SC': '100;300;400;500;700;900',
  'Noto Serif SC': '200;300;400;500;600;700;900',
  'Noto Sans TC': '100;300;400;500;700;900',
  'Noto Serif TC': '200;300;400;500;600;700;900',
  'Nanum Gothic': '400;700;800',
  'Nanum Myeongjo': '400;700;800',
  'Carlito': '400;700',
  'Caladea': '400;700',
  'Arimo': '400;700',
  'Tinos': '400;700',
  'Cousine': '400;700',
};

// ─────────────────────────────────────────────────────────────────────────────
// 폰트별 CSS line-height:normal 메트릭 (실측값)
// 출처: 각 폰트의 OS/2 winAscent + winDescent / unitsPerEm (fonttools로 추출)
//
// 이 값들은 CSS `line-height: normal` 이 해당 폰트에서 몇 %인지를 나타냄.
// PPTX에서 lnSpc가 없을 때(default=single) 이 값을 사용해야
// 원본 PPT의 줄간격과 정확히 일치함.
// ─────────────────────────────────────────────────────────────────────────────

/** 폰트명(소문자) → CSS line-height:normal 비율 (0.0 ~ 2.0) */
const FONT_LINE_HEIGHT_NORMAL: Record<string, number> = {
  // Official KoPub 2.0 hhea metrics: (850 - (-250)) / unitsPerEm 1000.
  'kopub돋움체 medium': 1.1,
  'kopub돋움체 bold': 1.1,
  'kopubdotum medium': 1.1,
  'kopubdotum bold': 1.1,
  // ── 한국어 Windows 폰트 (Malgun Gothic 계열) ──
  // 1.22 는 실측 보정값: LO(Noto Sans CJK KR 대체) 레퍼런스 라스터의 단일
  // 행간 피치 ≈ 1.19em, macOS PowerPoint(Apple SD Gothic Neo 대체)도 유사.
  // (Windows 원본 Malgun 의 win 메트릭 1.317 을 쓰면 대체 폰트 렌더에서
  //  문서 세로가 LO·PPT 보다 길어져 텍스트박스 하단이 잘린다 — portfolio.pptx
  //  실측. 하니스 SSIM 이 이 값의 회귀 게이트.)
  'malgun gothic':           1.22,
  '맑은 고딕':               1.22,
  'malgun gothic semilight': 1.22,
  'gulim':                   1.22,
  '굴림':                    1.22,
  'gulimche':                1.22,
  '굴림체':                  1.22,
  'dotum':                   1.22,
  '돋움':                    1.22,
  'dotumche':                1.22,
  '돋움체':                  1.22,
  'batang':                  1.22,
  '바탕':                    1.22,
  'batangche':               1.22,
  '바탕체':                  1.22,
  'gungsuh':                 1.22,
  '궁서':                    1.22,

  // ── 한국어 Google Fonts ──
  'noto sans kr':            1.448,  // winAscent=1160, winDescent=288, upm=1000
  'noto serif kr':           1.437,
  'nanum gothic':            1.300,
  'nanum myeongjo':          1.300,

  // ── 일본어 Windows 폰트 ──
  'ms gothic':               1.338,
  'ms pgothic':              1.338,
  'ｍｓ ｐゴシック':          1.338,
  'ms mincho':               1.338,
  'meiryo':                  1.320,
  'yu gothic':               1.320,

  // ── 일본어 Google Fonts ──
  'noto sans jp':            1.448,
  'noto serif jp':           1.437,

  // ── 중국어 Windows 폰트 ──
  'simsun':                  1.339,
  '宋体':                    1.339,
  'simhei':                  1.339,
  '黑体':                    1.339,
  'microsoft yahei':         1.288,
  '微软雅黑':                1.288,

  // ── 중국어 Google Fonts ──
  'noto sans sc':            1.448,
  'noto serif sc':           1.437,
  'noto sans tc':            1.448,
  'noto serif tc':           1.437,

  // ── 라틴 계열 ──
  // Office/Slides 계열은 실제 single line이 CSS font metric보다 넉넉하게 잡히는 편이라
  // metric-compatible fallback에도 1.20 기준을 사용한다.
  'arial':                   1.200,
  'arial narrow':            1.200,
  'arimo':                   1.200,
  'helvetica':               1.150,
  'helvetica neue':          1.150,
  'times new roman':         1.200,
  'tinos':                   1.200,
  'georgia':                 1.130,
  'verdana':                 1.180,
  'tahoma':                  1.200,
  'trebuchet ms':            1.200,
  'calibri':                 1.200,
  'carlito':                 1.200,
  'cambria':                 1.200,
  'caladea':                 1.200,
  'segoe ui':                1.310,
  'noto sans':               1.362,
  'courier new':             1.200,
  'cousine':                 1.200,

  // ── 장식 폰트 ──
  'lemon':                   1.200,
};

/** 폰트명 소문자 정규화 */
function normalizeFont(name: string): string {
  return name.trim().toLowerCase();
}

function resolveMetricFontKey(fontFamily?: string): string | undefined {
  if (!fontFamily) return undefined;
  const normalized = normalizeFont(fontFamily);
  // 원본 폰트명이 행간 테이블에 직접 있으면 그 값을 최우선으로 쓴다.
  // (종전에는 KR 폴백 맵이 먼저 가로채 'malgun gothic'→'noto sans kr'(1.448)로
  //  조회되는 바람에 맑은 고딕 계열의 보정 엔트리가 죽은 코드였고, 문서가
  //  원본·LO 레퍼런스보다 세로로 길어져 하단이 잘리는 사고가 났다.)
  if (FONT_LINE_HEIGHT_NORMAL[normalized] !== undefined) return normalized;
  return OFFICE_METRIC_FALLBACK_MAP[normalized]?.toLowerCase()
    || KOREAN_FONT_FALLBACK_MAP[normalized]?.toLowerCase()
    || JAPANESE_FONT_FALLBACK_MAP[normalized]?.toLowerCase()
    || CHINESE_FONT_FALLBACK_MAP[normalized]?.toLowerCase()
    || normalized;
}

export function getDefaultLineHeightRatio(fontFamily?: string): number {
  const DEFAULT = 1.317; // Malgun Gothic 기본값 (한국어 PPT 대부분)
  const key = resolveMetricFontKey(fontFamily);
  if (!key) return DEFAULT;
  return FONT_LINE_HEIGHT_NORMAL[key] ?? DEFAULT;
}

/**
 * 주어진 폰트의 CSS line-height:normal 에 해당하는 값을 반환.
 * PPTX lnSpc 없음(default=single) 일 때 이 값을 사용해야
 * 원본 PPT 줄간격과 일치.
 *
 * @param fontFamily - 폰트 이름 (원본 PPTX에 기재된 이름)
 * @returns CSS line-height 값 문자열 (예: "131.69%")
 */
export function getDefaultLineHeight(fontFamily?: string): string {
  const ratio = getDefaultLineHeightRatio(fontFamily);
  return `${(ratio * 100).toFixed(2)}%`;
}

/**
 * 폰트명을 받아 CSS font-family 폴백 체인 문자열을 반환.
 * 예: 'Malgun Gothic' → "'Malgun Gothic', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif"
 */
export function resolveFontFallback(fontName: string): string {
  const key = normalizeFont(fontName);

  // 이미 Google Fonts 폰트 → 그대로 + generic fallback
  if (GOOGLE_FONTS_MAP[key]) {
    return `'${fontName}', sans-serif`;
  }

  // Microsoft Office / Windows 대표 폰트는 metric-compatible 웹폰트 우선
  const officeFallback = OFFICE_METRIC_FALLBACK_MAP[key];
  if (officeFallback) {
    const serifVariant = officeFallback === 'Caladea' || officeFallback === 'Tinos';
    return serifVariant
      ? `'${fontName}', '${officeFallback}', serif`
      : `'${fontName}', '${officeFallback}', sans-serif`;
  }

  // 한국어 Windows 폰트. 외부 CDN 요청 없이 로컬/번들 폰트만 사용한다.
  // 라틴 글리프는 Noto Sans/Serif를 먼저 시도하고, 설치되지 않은 환경에서는
  // 뒤의 시스템 폰트와 CJK 폴백으로 자연스럽게 이어진다.
  const krFallback = KOREAN_FONT_FALLBACK_MAP[key];
  if (krFallback) {
    const serifVariant = krFallback.includes('Serif');
    return serifVariant
      ? `'${fontName}', 'Noto Serif', 'Apple SD Gothic Neo', '${krFallback}', serif`
      : `'${fontName}', 'Noto Sans', 'Apple SD Gothic Neo', '${krFallback}', 'Noto Sans KR', sans-serif`;
  }

  // 일본어 Windows 폰트
  const jpFallback = JAPANESE_FONT_FALLBACK_MAP[key];
  if (jpFallback) {
    return `'${fontName}', '${jpFallback}', sans-serif`;
  }

  // 중국어 Windows 폰트
  const cnFallback = CHINESE_FONT_FALLBACK_MAP[key];
  if (cnFallback) {
    return `'${fontName}', '${cnFallback}', sans-serif`;
  }

  // 시스템 폰트 → 그대로 사용
  if (SYSTEM_FONTS.has(key)) {
    return `'${fontName}', sans-serif`;
  }

  // 알 수 없는 폰트 → generic fallback
  return `'${fontName}', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif`;
}

/**
 * 해당 폰트를 위해 실제로 로드해야 할 Google Fonts 폰트명 반환.
 * 직접 사용하거나 fallback으로 필요한 폰트 포함.
 * 없으면 null.
 */
function getRequiredGoogleFont(fontName: string): string | null {
  const key = normalizeFont(fontName);

  // 직접 Google Fonts 폰트
  if (GOOGLE_FONTS_MAP[key]) return GOOGLE_FONTS_MAP[key];

  const officeFallback = OFFICE_METRIC_FALLBACK_MAP[key];
  if (officeFallback) return officeFallback;

  // fallback으로 Google Fonts 필요
  const fallback =
    KOREAN_FONT_FALLBACK_MAP[key] ||
    JAPANESE_FONT_FALLBACK_MAP[key] ||
    CHINESE_FONT_FALLBACK_MAP[key] ||
    NANUM_FONTS_MAP[key];

  return fallback || null;
}

/**
 * Samsung on-prem은 외부 Google Fonts를 요청하지 않는다. PPTX 뷰어는
 * 운영체제/번들 폰트 fallback만 사용한다.
 */
export function loadPptxFonts(usedFonts: string[]): void {
  void usedFonts;
}

/**
 * Windows 폰트명(예: 'Malgun Gothic')으로 font-family를 지정했을 때
 * 로컬에 없으면 Google Fonts로 연결되는 @font-face 주입.
 */
function injectLocalFontFaceAliases(usedFonts: string[], loadedGoogleFonts: Set<string>): void {
  const styleId = 'pptx-font-aliases';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  const rules: string[] = [];

  for (const fontName of usedFonts) {
    const key = normalizeFont(fontName);
    const googleFont =
      KOREAN_FONT_FALLBACK_MAP[key] ||
      JAPANESE_FONT_FALLBACK_MAP[key] ||
      CHINESE_FONT_FALLBACK_MAP[key];

    if (!googleFont || !loadedGoogleFonts.has(googleFont)) continue;

    // 이미 규칙이 있으면 중복 추가하지 않음
    if (styleEl.textContent?.includes(`'${fontName}'`)) continue;

    rules.push(
      `@font-face { font-family: '${fontName}'; src: local('${fontName}'); }`,
    );
  }

  if (rules.length > 0) {
    styleEl.textContent += '\n' + rules.join('\n');
  }
}
